/**
 * apps/web :: lib/rulediff-api
 *
 * Typed wrapper over `POST /api/rule-diff/generate` (ADR-0009 Tier-1
 * first caller). Always credentialed — the backend requires a valid
 * `rb_session` cookie and returns 401 otherwise.
 *
 * Wire shape mirrors backend `RuleDiffRequest` / `RuleDiffResponse`
 * (see backend/src/main/kotlin/cx/humane/roadbridge/rulediff/*). Keep
 * the field names aligned across the boundary; any divergence lands
 * here first so components consume a single stable type.
 */
import { apiFetch } from "@/lib/api";
import { getCountry } from "@/lib/countries";
import { getState, type UsStateCode } from "@/lib/states";
import type { UserProfile } from "@/lib/profile-api";

/** Request body for the generator endpoint. */
export interface RuleDiffRequestDto {
  homeCountry: string;
  targetState: string;
  targetLanguage: string;
  homeRuleSummary: string;
}

/** Successful response body. `tokenEstimate` is reserved for follow-up. */
export interface RuleDiffResponseDto {
  diff: string;
  providerId: string;
  latencyMs: number;
  tokenEstimate: number | null;
}

/**
 * Generate a rule-diff for the given request. On non-2xx the underlying
 * `apiFetch` throws an `ApiError` — callers decide whether to show an
 * inline error message or bounce to `/login` on 401.
 */
export async function generateRuleDiff(
  req: RuleDiffRequestDto,
): Promise<RuleDiffResponseDto> {
  return apiFetch<RuleDiffResponseDto>("/api/rule-diff/generate", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

/**
 * Derive a rule-diff request from a stored `UserProfile` + a target US
 * state, then call {@link generateRuleDiff}. Centralises the mapping of
 * profile wire fields (ISO alpha-2 country code, `LangCode`, state code)
 * onto the generator's request shape so each caller isn't re-implementing
 * the mapping.
 *
 * Fallbacks:
 *   - The generator expects a free-text `homeCountry` and `targetState`. We
 *     resolve the catalog entry when available — that keeps the model
 *     prompt friendly ("India" / "California" rather than "IN" / "CA") —
 *     and fall through to the raw code if the catalog misses (the backend
 *     still validates the codes on its own allow-list).
 *   - `homeRuleSummary` is a server-required field for the Tier-1 path.
 *     Today we have no user-authored summary on file, so we synthesise a
 *     minimal placeholder derived from the profile. This is a known gap
 *     called out in the PR body; ROADS-147+ will replace it with a proper
 *     per-country rule summary (either authored or retrieved from the
 *     content ops corpus).
 */
export async function generateRuleDiffForProfile(
  profile: UserProfile,
  targetState: UsStateCode,
): Promise<RuleDiffResponseDto> {
  const country = getCountry(profile.home_country);
  const state = getState(targetState);
  const homeCountryLabel = country?.name ?? profile.home_country;
  const targetStateLabel = state?.name ?? targetState;
  return generateRuleDiff({
    homeCountry: homeCountryLabel,
    targetState: targetStateLabel,
    targetLanguage: profile.native_language,
    // Placeholder summary — see the docstring above. The backend treats this
    // as the learner-authored (or curated) summary of their home-country
    // rules; an empty string is rejected with 400, so we always ship at
    // least one sentence derived from the profile.
    homeRuleSummary: `Summary of ${homeCountryLabel} driving rules for a learner studying in ${targetStateLabel}.`,
  });
}

